import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { usePermissions } from "../hooks/usePermissions";
import { firstAvailableRoute } from "../config/solutions";
import { logRouteDiagnostic } from "../utils/blankPageDiagnostics";

/**
 * Pathless layout route that gates every child route on a solution.
 *
 * Used where a section mixes solutions under one URL prefix — /commerce/* holds Revenue
 * routes alongside Catalog, Parties, Procurement and Governance, so the prefix itself
 * cannot carry the gate. Wrapping the Revenue blocks keeps the sibling routes reachable.
 *
 *   <Route element={<SolutionGuard solution="revenue" />}>
 *     <Route path="revenue/commits" ... />
 *   </Route>
 *
 * UX only. The backend's require_solution() is the enforcement boundary; a user who edits
 * the URL is rejected by the API regardless of what this renders.
 */
export default function SolutionGuard({ solution }) {
  const { hasSolution } = usePermissions();

  if (!hasSolution(solution)) {
    const fallback = firstAvailableRoute(hasSolution);
    logRouteDiagnostic("SolutionGuard_denied_redirecting", {
      solution,
      fallback,
    });
    return <Navigate to={fallback} replace />;
  }

  return <Outlet />;
}
